import { Create } from "@refinedev/mui";
import {
  Box,
  TextField,
  Grid,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { useWatch } from "react-hook-form";
import { IResourceComponentsProps, useTranslate } from "@refinedev/core";
import React, { useEffect } from "react";

export const DiscountCreate: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();
  const {
    saveButtonProps,
    refineCore: { formLoading },
    register,
    control,
    formState: { errors },
    setValue,
  } = useForm();

  // Watch for discount type changes
  const discountType = useWatch({
    control,
    name: "type",
    defaultValue: "percentage",
  });

  // Watch for applies to products (ensure it's always an array)
  const appliesToValue = useWatch({
    control,
    name: "appliesTo",
    defaultValue: [],
  });

  const currencies = [
    { value: "usd", label: "USD - US Dollar" },
    { value: "eur", label: "EUR - Euro" },
    { value: "gbp", label: "GBP - British Pound" },
    { value: "cad", label: "CAD - Canadian Dollar" },
  ];

  // Sample product list - In a real app, this would come from an API
  const availableProducts = [
    { id: "prod_premium", name: "Premium Plan" },
    { id: "prod_basic", name: "Basic Plan" },
    { id: "prod_enterprise", name: "Enterprise Plan" },
  ];

  // Register the appliesTo field with default value
  useEffect(() => {
    register("appliesTo", { value: [] });
  }, [register]);

  return (
    <Create isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column" }}
        autoComplete="off"
      >
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("name", {
                    required: translate('common.required'),
                  })}
                  label={translate('discounts.fields.name')}
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message as string}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Description */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("description")}
                  label={translate('discounts.fields.description')}
                  fullWidth
                  multiline
                  rows={3}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Discount Type */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <FormControl fullWidth>
                  <InputLabel>{translate('discounts.fields.type')}</InputLabel>
                  <Select
                    {...register("type", {
                      required: translate('common.required'),
                    })}
                    label={translate('discounts.fields.type')}
                    defaultValue="percentage"
                    error={!!errors.type}
                  >
                    <MenuItem value="percentage">{translate('discounts.type.percentage')}</MenuItem>
                    <MenuItem value="amount">{translate('discounts.type.amount')}</MenuItem>
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Percentage Field (conditional) */}
          {discountType === "percentage" && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <TextField
                    {...register("percentage", {
                      required: discountType === "percentage" ? translate('common.required') : false,
                      min: { value: 0, message: translate('discounts.validation.percentageMin') },
                      max: { value: 100, message: translate('discounts.validation.percentageMax') },
                    })}
                    label={translate('discounts.fields.percentage')}
                    type="number"
                    fullWidth
                    error={!!errors.percentage}
                    helperText={errors.percentage?.message as string}
                    InputProps={{
                      endAdornment: "%"
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Amount and Currency Fields (conditional) */}
          {discountType === "amount" && (
            <>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <TextField
                      {...register("amount", {
                        required: discountType === "amount" ? translate('common.required') : false,
                        min: { value: 1, message: translate('discounts.validation.amountMin') },
                      })}
                      label={translate('discounts.fields.amount')}
                      type="number"
                      fullWidth
                      error={!!errors.amount}
                      helperText={errors.amount?.message as string}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <FormControl fullWidth>
                      <InputLabel>{translate('discounts.fields.currency')}</InputLabel>
                      <Select
                        {...register("currency", {
                          required: discountType === "amount" ? translate('common.required') : false,
                        })}
                        label={translate('discounts.fields.currency')}
                        defaultValue="usd"
                        error={!!errors.currency}
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>
              </Grid>
            </>
          )}

          {/* Expiration Date */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("redeemBy")}
                  label={translate('discounts.fields.expirationDate')}
                  type="datetime-local"
                  fullWidth
                  disabled={discountType === "amount"}
                  error={!!errors.redeemBy}
                  helperText={
                    discountType === "amount"
                      ? translate('discounts.validation.expirationNotAvailable')
                      : (errors.redeemBy?.message as string || translate('discounts.validation.noExpiration'))
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Max Redemptions */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <TextField
                  {...register("maxRedemptions", {
                    min: { value: 1, message: translate('discounts.validation.maxRedemptionsMin') },
                  })}
                  label={translate('discounts.fields.maxRedemptions')}
                  type="number"
                  fullWidth
                  error={!!errors.maxRedemptions}
                  helperText={errors.maxRedemptions?.message as string || translate('discounts.validation.unlimitedUses')}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Applies To Products */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <FormControl fullWidth>
                  <InputLabel>{translate('discounts.fields.appliesTo')}</InputLabel>
                  <Select
                    name="appliesTo"
                    value={appliesToValue || []}
                    onChange={(event) => {
                      const value = event.target.value;
                      setValue("appliesTo", typeof value === 'string' ? value.split(',') : value);
                    }}
                    multiple
                    input={<OutlinedInput label={translate('discounts.fields.appliesTo')} />}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as string[]).map((value) => {
                          const product = availableProducts.find(p => p.id === value);
                          return (
                            <Chip key={value} label={product?.name || value} />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {availableProducts.map((product) => (
                      <MenuItem key={product.id} value={product.id}>
                        {product.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
          </Grid>

          {/* Active Status */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <FormControlLabel
                  control={
                    <Switch
                      {...register("active")}
                      defaultChecked
                    />
                  }
                  label={translate('discounts.fields.active')}
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Create>
  );
};
